<template>
    <!-- Breadcrumb Navigation -->
    <div class="tw-p-2 tw-bg-white tw-w-full tw-border tw-border-solid tw-flex tw-items-center tw-gap-2">
        <router-link :to="{ name: 'pages.admin.dashboard' }"
            class="tw-text-gray-400 tw-duration-200 hover:tw-text-gray-700 tw-text-xl tw-flex tw-items-center tw-gap-2">
            <icon icon="solar:home-smile-bold-duotone" /> 
            <span class="tw-text-sm">{{ $t("dashboard.sidebar.dashboard") }}</span>
        </router-link>
        <span class="tw-text-gray-300">/</span>
        <button disabled class="tw-text-primary-500 tw-text-xl tw-flex tw-items-center tw-gap-2">
            <span class="tw-text-sm">{{ $t("dashboard.sidebar.services") }}</span>
        </button>
    </div>

    <!-- Main Content -->
    <div class="tw-p-2">
        <div class="tw-h-full tw-w-full tw-p-4x tw-bg-whitex tw-rounded tw-border-b-2 tw-min-h-[calc(100dvh-125px)]">
            
            <!-- Header Section -->
            <div class="tw-flex tw-items-center tw-justify-between tw-mb-2">
                <div class="tw-flex tw-items-center tw-gap-4">
                    <!-- Search Field -->
                    <FormField 
                        v-model="search" 
                        type="text" 
                        :hide-label="true" 
                        :hide-errors="true" 
                        :placeholder="$t('common.services-search-placeholder', 'Search services...')"
                        left-icon="solar:magnifer-linear" 
                        @input="onSearch" 
                        class="tw-bg-white tw-rounded-md tw-min-w-[300px]" 
                    />

                    <!-- Status Filter -->
                    <FormSelect
                    v-if="false" 
                        v-model="filters.status" 
                        :options="statusOptions"
                        :placeholder="$t('common.filter-by-status', 'Filter by status')"
                        left-icon="solar:filter-bold-duotone" 
                        class="tw-bg-white tw-rounded-md tw-min-w-[150px]"
                    />

                    <!-- Category Filter -->
                    <FormSelect
                    v-if="false" 
                        v-model="filters.category" 
                        :options="categoryOptions"
                        :placeholder="$t('common.filter-by-category', 'Filter by category')"
                        left-icon="solar:widget-bold-duotone" 
                        class="tw-bg-white tw-rounded-md tw-min-w-[200px]"
                    />

                    <!-- Company Filter -->
                    <FormSelect
                    v-if="false" 
                        v-model="filters.company" 
                        :options="companyOptions"
                        :placeholder="$t('common.filter-by-company', 'Filter by company')"
                        left-icon="solar:buildings-bold-duotone" 
                        class="tw-bg-white tw-rounded-md tw-min-w-[200px]"
                    />
                </div>

                <!-- Create Service Button -->
                <div>
                    <a-button 
                        as="router-link" 
                        :to="{ name: 'pages.admin.services.create' }" 
                        intent="flat" 
                        left-icon="solar:add-circle-bold-duotone"
                        class="tw-min-w-[160px]"
                    >
                        {{ $t('common.create-new-service', 'Create New Service') }}
                    </a-button>
                </div>
            </div>
            
            <!-- Services Table -->
            <AdminServiceTable 
                :loading="loading" 
                :items="services" 
                :options="paginationOptions" 
                @next="onNext"
                @previous="onPrevious" 
                @edit="onEdit" 
                @delete="onDelete" 
            />
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import FormField from '@/components/form/FormField.vue';
import FormSelect from '@/components/form/FormSelect.vue';
import AdminServiceTable from '@/components/dashboard/admin/services/table/AdminServiceTable.vue';
import * as serviceApi from '@/api/services';
import useAlert from '@/composables/useAlert';

const { t } = useI18n();

// Reactive data
const loading = ref(false);
const services = ref([]);
const search = ref('');

// Pagination options
const paginationOptions = ref({
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
    next_page_url: null,
    prev_page_url: null
});

// Filters
const filters = reactive({
    status: '',
    category: '',
    company: ''
});

// Filter options
const statusOptions = ref([
    { id: '', name: t('common.all') },
    { id: 'active', name: t('common.active') },
    { id: 'inactive', name: t('common.inactive') }
]);

const categoryOptions = ref([
    { id: '', name: t('common.all') }
]);

const companyOptions = ref([
    { id: '', name: t('common.all') }
]);

// Search debounce
let searchTimeout = null;
let filterTimeout = null;

// Watch for filter changes
watch(() => filters.status, () => {
    clearTimeout(filterTimeout);
    filterTimeout = setTimeout(() => {
        fetchServices(1);
    }, 300);
}, { immediate: false });

watch(() => filters.category, () => {
    clearTimeout(filterTimeout);
    filterTimeout = setTimeout(() => {
        fetchServices(1);
    }, 300);
}, { immediate: false });

watch(() => filters.company, () => {
    clearTimeout(filterTimeout);
    filterTimeout = setTimeout(() => {
        fetchServices(1);
    }, 300);
}, { immediate: false });

// Methods
const fetchServices = async (page = 1) => {
    loading.value = true;

    try {
        const params = {
            page,
            per_page: paginationOptions.value.per_page,
            search: search.value,
            ...filters
        };

        // Remove empty filters
        Object.keys(params).forEach(key => {
            if (params[key] === '' || params[key] === null || params[key] === undefined) {
                delete params[key];
            }
        });

        // Use admin API if available, otherwise fall back to regular API
        const response = serviceApi.admin && serviceApi.admin.paginate 
            ? await serviceApi.admin.paginate(params)
            : await serviceApi.paginate(params);

        if (response.data && response.data.data) {
            services.value = response.data.data;
            paginationOptions.value = {
                current_page: response.data.current_page,
                per_page: response.data.per_page,
                total: response.data.total,
                last_page: response.data.last_page,
                next_page_url: response.data.next_page_url,
                prev_page_url: response.data.prev_page_url
            };
        }
    } catch (error) {
        console.error('Error fetching services:', error);
        useAlert(t('errors.something-went-wrong', 'Something went wrong'), 'danger');
    } finally {
        loading.value = false;
    }
};

const onSearch = () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
        fetchServices(1);
    }, 500);
};

const onNext = () => {
    if (paginationOptions.value.current_page < paginationOptions.value.last_page) {
        fetchServices(paginationOptions.value.current_page + 1);
    }
};

const onPrevious = () => {
    if (paginationOptions.value.current_page > 1) {
        fetchServices(paginationOptions.value.current_page - 1);
    }
};

const onEdit = (service) => {
    // Edit is handled by the action button routing
    console.log('Edit service:', service.id);
};

const onDelete = (service) => {
    // Delete functionality can be implemented here if needed
    console.log('Delete service:', service.id);
};

// Fetch filter options (categories and companies)
const fetchFilterOptions = async () => {
    try {
        // You can implement these API calls if they exist
        // For now, we'll keep the basic options
        categoryOptions.value = [
            { id: '', name: t('common.all') }
        ];
        
        companyOptions.value = [
            { id: '', name: t('common.all') }
        ];
    } catch (error) {
        console.error('Error fetching filter options:', error);
    }
};

// Initialize
onMounted(() => {
    fetchServices();
    fetchFilterOptions();
});
</script>

<style scoped>
/* Add any custom styles here */
</style>